import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { UIApplication, CompanyDocument, UIPosition } from 'types';

type PositionsContextType = {
	eligiblePositions: UIPosition[];
	inEligiblePositions: UIPosition[];
	closedPositions: UIPosition[];
	activeApplicantCount: number;
};

export interface PositionContextOptions {
	positions: PositionsContextType;
	applications: UIApplication[];
	isLoadingPositions: boolean;
	unlockedCompanies: CompanyDocument[];
	totalCompanies: number;
	setIsLoadingPositions: React.Dispatch<React.SetStateAction<boolean>>;
	setApplications: React.Dispatch<React.SetStateAction<UIApplication[]>>;
	setPositions: React.Dispatch<React.SetStateAction<PositionsContextType>>;
}
export interface PositionContextProps {
	children: ReactNode;
}

const PositionContext = createContext<PositionContextOptions>({
	positions: { eligiblePositions: [], inEligiblePositions: [], closedPositions: [], activeApplicantCount: 0 },
	applications: [],
	setApplications: () => {},
	setPositions: () => {},
	isLoadingPositions: false,
	setIsLoadingPositions: () => {},
	totalCompanies: 0,
	unlockedCompanies: [],
});

export const PositionContextProvider = ({ children }: PositionContextProps) => {
	const [applications, setApplications] = useState<UIApplication[]>([]);
	const [isLoadingPositions, setIsLoadingPositions] = useState<boolean>(false);
	const [unlockedCompanies, setUnlockedCompanies] = useState<CompanyDocument[]>([]);
	const [totalCompanies, setTotalCompanies] = useState<number>(0);
	const [positions, setPositions] = useState<PositionsContextType>({
		eligiblePositions: [],
		inEligiblePositions: [],
		closedPositions: [],
		activeApplicantCount: 0,
	});

	useEffect(() => {
		setUnlockedCompanies(
			positions.eligiblePositions.reduce((acc: CompanyDocument[], curr) => {
				if (!acc.map(a => String(a._id)).includes(String(curr.company._id))) {
					acc.push(curr.company);
				}
				return acc;
			}, []),
		);
	}, [positions.eligiblePositions]);
	useEffect(() => {
		setTotalCompanies(
			positions.eligiblePositions.concat(positions.inEligiblePositions).reduce((acc: string[], curr) => {
				if (!acc.includes(String(curr.company._id))) {
					acc.push(String(curr.company._id));
				}
				return acc;
			}, []).length,
		);
	}, [positions]);

	return (
		<PositionContext.Provider
			value={{
				positions,
				applications,
				setApplications,
				setPositions,
				isLoadingPositions,
				setIsLoadingPositions,
				unlockedCompanies,
				totalCompanies,
			}}
		>
			{children}
		</PositionContext.Provider>
	);
};

export const usePositionContext = () => {
	const context = useContext(PositionContext);
	return context;
};
